import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import PdfPreview from '@/app/components/cards/FileCard/PdfPreview';
import type { UploadFileMeta } from '@/context/UploadFileProvider/types';
import LoaderSmall from '/public/icons/loader-small.svg';
import NoImageSvgIcon from '/public/icons/no-image.svg';
import TrashIcon from '/public/icons/trash.svg';

interface UploadFileProgressItemProps {
  upload: UploadFileMeta;
  onCancel: (fileName: string) => void;
}

export function UploadFileProgressItem({ upload, onCancel }: UploadFileProgressItemProps) {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [hasImageError, setHasImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const previewUrl = useMemo(() => {
    if (!upload.file) return '';
    return URL.createObjectURL(upload.file);
  }, [upload.file]);

  const isSuccess = upload.status === 'success';

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    setIsImageLoading(false);
    setHasImageError(false);
  }, [previewUrl]);

  const isImage = upload.file.type.startsWith('image/');
  const isPdf = upload.file.type === 'application/pdf';

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setHasImageError(false);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
    setHasImageError(true);
  };

  useEffect(() => {
    if (isImage && previewUrl) {
      setIsImageLoading(true);
    }
  }, [isImage, previewUrl]);

  if (upload.status === 'canceled') return null;

  return (
    <div className="flex items-center gap-[14px]">
      {/* Image Preview */}
      {isImage && previewUrl && (
        <div className="relative w-[44px] h-[44px] flex items-center justify-center">
          {isImageLoading && (
            <div className="absolute w-full h-full inset-0 flex items-center justify-center bg-gray-800 bg-opacity-25 rounded-[6px] z-10">
              <LoaderSmall className="w-5 h-5 text-white animate-spin" />
            </div>
          )}
          {!hasImageError ? (
            <Image
              ref={imgRef}
              src={previewUrl}
              alt={upload.file.name}
              className="w-10 h-10 rounded-[6px] object-cover"
              width={44}
              height={44}
              style={{ display: isImageLoading ? 'none' : 'block' }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : (
            <NoImageSvgIcon className="w-10 h-10 text-red-500" />
          )}
        </div>
      )}
      {/* PDF Preview */}
      {isPdf && previewUrl && (
        <div className="relative w-[44px] h-[44px] flex items-center justify-center">
          <PdfPreview fileUrl={previewUrl} containerWidth={44} containerHeight={44} />
        </div>
      )}
      {/* File Info and Progress */}
      <div className="flex items-center gap-2 justify-between w-full">
        <div className="flex flex-col items-center gap-2">
          <div className="text-body-small text-black truncate w-[250px] min-w-[250px] max-w-[250px]">
            {upload.file.name}
          </div>
          <div className="w-full h-2 mt-1 bg-gray-200 rounded">
            <div
              className="h-2 bg-status-success rounded transition-all duration-300"
              style={{ width: `${upload.progress}%` }}
            />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {!isSuccess && (
            <button onClick={() => onCancel(upload.file.name)} className="ml-2 cursor-pointer">
              <TrashIcon className="w-[20px] h-[20px] fill-current text-red-500" />
            </button>
          )}
          <div className="text-right text-button-regular text-neutral-700">{upload.progress}%</div>
        </div>
      </div>
    </div>
  );
}